<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { showError, showSuccess } from '@/services/notification'
import { storeToRefs } from 'pinia'
import ImpersonationBanner from '@/components/ImpersonationBanner.vue'
import NotificationBell from '@/components/NotificationBell.vue'
import UpcomingMaintenanceBanner from '@/components/UpcomingMaintenanceBanner.vue'
import BalanceDisplay from '@/components/BalanceDisplay.vue'
import maintenanceRedirectService from '@/services/maintenanceRedirectService'
import * as Sentry from '@sentry/vue'
import { checkSentryHealth } from '@/utils/sentry'


const handleRouterLinkClick = (event, to) => {
  const currentPath = router.currentRoute.value.path
  if (currentPath === to) {
    event.preventDefault()
    window.location.href = to
  }
}
const router = useRouter()
const authStore = useAuthStore()
const teamsStore = useTeamsStore()
const { isAuthenticated, isSuperAdmin, user } = storeToRefs(authStore)

const loading = ref(true)
const menuActive = ref(false)
const isMaintenanceBannerDismissed = ref(false)
const isDevSentryEnv = import.meta.env.VITE_SENTRY_ENV === 'dev'
const isDevBannerDismissed = ref(false)
const devTitlePrefix = 'DEV | '

const openSentryFeedback = async () => {
  
  const feedback = Sentry.getFeedback()
  const form = await feedback?.createForm()
  form.appendToDom()
  form.open()
}
const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/')
    menuActive.value = false
  } catch (error) {
    console.error('Fehler beim Logout:', error)
    showError(error.message)
  }
}

const checkMaintenanceBannerStatus = () => {
  try {
    const dismissedData = localStorage.getItem('maintenance-banner-dismissed')
    if (dismissedData) {
      const parsed = JSON.parse(dismissedData)
      isMaintenanceBannerDismissed.value = !!parsed.startDate
    } else {
      isMaintenanceBannerDismissed.value = false
    }
  } catch (error) {
    console.error('Error parsing dismissed maintenance data:', error)
    isMaintenanceBannerDismissed.value = false
  }
}

const dismissDevBanner = () => {
  isDevBannerDismissed.value = true
}

const showMaintenanceBanner = () => {
  if (typeof window !== 'undefined' && window.resetMaintenanceBanner) {
    window.resetMaintenanceBanner()
    isMaintenanceBannerDismissed.value = false
  }
}

const checkAuth = async () => {
  try {
    await authStore.init()
  } catch (error) {
    console.error('Auth error:', error)
    if (error.message) {
      showError(error.message)
    }
  } finally {
    loading.value = false
  }
}

const applyDevTitle = () => {
  if (!isDevSentryEnv || typeof document === 'undefined') return
  const currentTitle = document.title || 'Quitly'
  if (!currentTitle.startsWith(devTitlePrefix)) {
    document.title = `${devTitlePrefix}${currentTitle}`
  }
}
const sentryHealth = ref(false)
let maintenanceCheckInterval = null

onMounted(async () => {

  applyDevTitle()
  await checkAuth()
  sentryHealth.value = await checkSentryHealth()
  checkMaintenanceBannerStatus()
  isDevBannerDismissed.value = false
  
  // Listen for localStorage changes
  window.addEventListener('storage', (e) => {
    if (e.key === 'maintenance-banner-dismissed') {
      checkMaintenanceBannerStatus()
    }
  })
  
  // Listen for custom maintenance banner events
  window.addEventListener('maintenance-banner-dismissed', checkMaintenanceBannerStatus)
  
  // Start maintenance check after auth is loaded
  if (isAuthenticated.value) {
    maintenanceCheckInterval = maintenanceRedirectService.startPeriodicCheck(router, authStore)
  }
})

onUnmounted(() => {
  if (maintenanceCheckInterval) {
    clearInterval(maintenanceCheckInterval)
  }
  
  // Remove event listeners
  window.removeEventListener('maintenance-banner-dismissed', checkMaintenanceBannerStatus)
})

// Watch for route changes to reset teams pagination when leaving ReceiptWizardView
watch(
  () => router.currentRoute.value.path,
  (newPath, oldPath) => {
    // If coming from ReceiptWizardView (which uses limited teams loading)
    // and going to a teams-related route, reset teams pagination
    if (oldPath && oldPath.includes('/receipt-wizard') && 
        (newPath.includes('/teams') || newPath.includes('/bereiche'))) {
      teamsStore.resetToDefaultPagination()
    }
  }
)

const toggleMenu = () => {
  menuActive.value = !menuActive.value
  if (menuActive.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

const closeMenu = () => {
  menuActive.value = false
  document.body.style.overflow = ''
}
</script>

<template>
  <!-- Upcoming Maintenance Banner -->
  <div id="modalTarget"></div>
  <!-- Loading Screen -->
  <div v-if="loading" class="loading-screen">
    <div class="loading-content">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
      <p class="mt-3 text-muted">Quitly wird geladen...</p>
    </div>
  </div>

  <!-- Main App -->
  <div v-else class="app-wrapper">
    <div
      v-if="isDevSentryEnv && !isDevBannerDismissed"
      class="dev-env-banner"
      role="status"
      aria-live="polite"
    >
      <span class="dev-env-banner__text">DEV DEV DEV</span>
      <button
        class="btn btn-sm btn-outline-dark dev-env-banner__close"
        type="button"
        @click="dismissDevBanner"
        aria-label="DEV-Banner ausblenden"
      >
        <font-awesome-icon icon="times" />
      </button>
    </div>
    <ImpersonationBanner />
    <div :class="['mobile-nav-overlay', { active: menuActive }]">
      <div class="mobile-nav-content">
        <!-- Mobile Balance Display -->
        <div v-if="isAuthenticated" class="mobile-balance-section">
          <BalanceDisplay />
        </div>
        
        <div class="options">
          <div class="option" v-if="isAuthenticated">
            <router-link @click="(e) => { closeMenu(); handleRouterLinkClick(e, '/dashboard') }" class="nav-link" to="/dashboard">
              <font-awesome-icon icon="tachometer-alt" class="me-2" />Dashboard
            </router-link>
          </div>
          <div class="option" v-if="isAuthenticated">
            <router-link @click="(e) => { closeMenu(); handleRouterLinkClick(e, '/teams') }" class="nav-link" to="/teams">
              <font-awesome-icon icon="users" class="me-2" />Bereiche
            </router-link>
          </div>
          <div class="option" v-if="isAuthenticated">
            <router-link @click="(e) => { closeMenu(); handleRouterLinkClick(e, '/receipts') }" class="nav-link" to="/receipts">
              <font-awesome-icon icon="receipt" class="me-2" />Quittungen
            </router-link>
          </div>
          <div class="option" v-if="isAuthenticated">
            <router-link @click="(e) => { closeMenu(); handleRouterLinkClick(e, '/account') }" class="nav-link" to="/account">
              <font-awesome-icon icon="credit-card" class="me-2" />Account
            </router-link>
          </div>
          <div class="option" v-if="isAuthenticated">
            <router-link @click="(e) => { closeMenu(); handleRouterLinkClick(e, '/notifications') }" class="nav-link" to="/notifications">
              <font-awesome-icon icon="bell" class="me-2" />Benachrichtigungen
            </router-link>
          </div>
          <div class="option" v-if="isAuthenticated">
            <router-link @click="(e) => { closeMenu(); handleRouterLinkClick(e, '/profile') }" class="nav-link" to="/profile">
              <font-awesome-icon icon="user" class="me-2" />Profil
            </router-link>
          </div>
          <div class="option" v-if="isAuthenticated && isSuperAdmin">
            <router-link @click="(e) => { closeMenu(); handleRouterLinkClick(e, '/admin') }" class="nav-link" to="/admin">
              <font-awesome-icon icon="cog" class="me-2" />Admin
            </router-link>
          </div>

          <div class="option" v-if="!isAuthenticated">
            <router-link @click="closeMenu" to="/auth/login" class="mobile-nav-btn">
              <font-awesome-icon icon="sign-in-alt" class="me-2" />Anmelden
            </router-link>
          </div>
          <div class="option" v-if="!isAuthenticated">
            <router-link
              @click="closeMenu"
              to="/auth/register"
              class="mobile-nav-btn mobile-nav-btn-primary"
            >
              <font-awesome-icon icon="user-plus" class="me-2" />Registrieren
            </router-link>
          </div>
          <div class="option" v-if="isAuthenticated">
            <button @click="handleLogout" class="mobile-nav-btn mobile-nav-btn-danger">
              <font-awesome-icon icon="sign-out-alt" class="me-2" />Abmelden
            </button>
          </div>
        </div>
      </div>
    </div>

    <nav :class="['navbar', 'navbar-expand-lg', 'py-3', 'px-5', 'w-100']">
      <div class="container-fluid">
        <router-link class="navbar-brand" to="/">
          <img src="@/assets/img/quitly.svg" alt="Quitly" class="navbar-logo" />
        </router-link>

        <div class="desktop-menu">
          <ul class="navbar-nav me-auto">
            <li class="nav-item" v-if="isAuthenticated">
              <router-link @click="(e) => handleRouterLinkClick(e, '/dashboard')" class="nav-link" to="/dashboard">
                <font-awesome-icon icon="tachometer-alt" class="me-2" />Dashboard
              </router-link>
            </li>
            <li class="nav-item" v-if="isAuthenticated">
              <router-link @click="(e) => handleRouterLinkClick(e, '/receipts')" class="nav-link" to="/receipts">
                <font-awesome-icon icon="receipt" class="me-2" />Quittungen
              </router-link>
            </li>
            <li class="nav-item" v-if="isAuthenticated">
              <router-link @click="(e) => handleRouterLinkClick(e, '/teams')" class="nav-link" to="/teams">
                <font-awesome-icon icon="users" class="me-2" />Bereiche
              </router-link>
            </li>
            <li class="nav-item" v-if="isAuthenticated">
              <router-link @click="(e) => handleRouterLinkClick(e, '/account')" class="nav-link" to="/account">
                <font-awesome-icon icon="credit-card" class="me-2" />Account
              </router-link>
            </li>
            <li class="nav-item" v-if="isAuthenticated">
              <router-link @click="(e) => handleRouterLinkClick(e, '/profile')" class="nav-link" to="/profile">
                <font-awesome-icon icon="user" class="me-2" />Profil
              </router-link>
            </li>
            <li
              class="nav-item"
              v-if="isAuthenticated && isSuperAdmin && !authStore.isImpersonated()"
            >
              <router-link @click="(e) => handleRouterLinkClick(e, '/admin')" class="nav-link" to="/admin">
                <font-awesome-icon icon="cog" class="me-2" />Admin
              </router-link>
            </li>
          </ul>
          <div class="d-flex align-items-center">
            <template v-if="!isAuthenticated">
              <router-link to="/auth/login" class="btn btn-outline-primary me-2">
                <font-awesome-icon icon="sign-in-alt" class="me-1" />Anmelden
              </router-link>
              <router-link to="/auth/register" class="btn btn-primary">
                <font-awesome-icon icon="user-plus" class="me-1" />Registrieren
              </router-link>
            </template>
            <template v-else>
              <div class="user-menu">
                <div class="user-actions">
                  <div class="account-group">
                    <BalanceDisplay class="me-2" />
                    <NotificationBell class="me-2" />
                  </div>
                  <div class="user-info" v-if="user?.firstName && user?.lastName">
                    <small class="text-white">{{ user.firstName }} {{ user.lastName }}</small>
                  </div>
                </div>
                <button class="btn btn-outline-danger btn-sm ms-auto" @click="handleLogout">
                  <font-awesome-icon icon="sign-out-alt" class="me-1" />Abmelden
                </button>
              </div>
            </template>
          </div>
        </div>

        <div :class="['hamburger-menu', { active: menuActive }]" @click="toggleMenu">
          <span class="hamburger-icon">
            <font-awesome-icon :icon="menuActive ? 'times' : 'bars'" />
          </span>
        </div>
      </div>
    </nav>
    <div class="viewport">
      <UpcomingMaintenanceBanner />
      <router-view></router-view>
      <footer class="mt-auto py-3">
        <div class="container">
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-center">
            <div class="mb-2 mb-md-0">
              <span>&copy; {{ new Date().getFullYear() }} Quitly. Alle Rechte vorbehalten.</span>
            </div>
            <div class="btn-group" role="group" v-if="sentryHealth || isMaintenanceBannerDismissed">
              <button v-if="sentryHealth" @click="openSentryFeedback" class="btn btn-sm btn-outline-secondary">
                <font-awesome-icon :icon="['fas','bug']" class="me-1" />
                Bug entdeckt?
              </button>
              <button v-if="isMaintenanceBannerDismissed" @click="showMaintenanceBanner" class="btn btn-sm btn-outline-secondary" title="Wartungsmodus-Banner anzeigen">
                <font-awesome-icon icon="bell" class="me-1" />
                Wartung anzeigen
              </button>
            </div>
            <div class="d-flex gap-3">
              <a href="https://quitly.de/kontakt/" class="footer-link" target="_blank">Kontakt</a>
              <a href="https://quitly.de/datenschutz/" class="footer-link" target="_blank"
                >Datenschutz</a
              >
              <a href="https://quitly.de/datenschutz/agb/" class="footer-link" target="_blank">Nutzungsbedingungen</a>
              <a href="https://quitly.de/datenschutz/widerrufsbelehrung/" class="footer-link" target="_blank">Widerrufsbelehrung</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
    
    <!-- Teleport target for modals -->
    <div id="modalTarget"></div>
  </div>
</template>

<style scoped>
.footer-link.btn {
  border: 1px solid var(--text-muted);
  color: var(--text-muted);
  background: transparent;
  font-size: 0.9rem;
  padding: 0.25rem 0.5rem;
  text-decoration: none;
  transition: all 0.3s ease;
}

.footer-link.btn:hover {
  color: var(--quitly-primary);
  border-color: var(--quitly-primary);
  background: transparent;
  text-decoration: none;
}

.footer-link.btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.dev-env-banner {
  position: sticky;
  top: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 3rem;
  color: #131313;
  background: #f9c846;
  letter-spacing: 0.45rem;
  font-weight: 800;
  text-transform: uppercase;
  border-bottom: 2px solid rgba(0, 0, 0, 0.25);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.dev-env-banner::before {
  content: "";
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.18) 0,
    rgba(0, 0, 0, 0.18) 10px,
    rgba(0, 0, 0, 0) 10px,
    rgba(0, 0, 0, 0) 20px
  );
  pointer-events: none;
}

.dev-env-banner__text {
  position: relative;
  z-index: 1;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.35);
}

.dev-env-banner__close {
  position: absolute;
  right: 0.75rem;
  z-index: 1;
  color: #111;
  line-height: 1;
  padding: 0.15rem 0.45rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.dev-env-banner__close:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.dev-env-banner__close:focus-visible {
  outline: 2px solid rgba(0, 0, 0, 0.6);
  outline-offset: 2px;
}

/* Mobile Balance Section */
.mobile-balance-section {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 0.5rem;
}

.mobile-balance-section .balance-display {
  justify-content: center;
  gap: 0.5rem;
}

.mobile-balance-section .balance-item {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.75rem 1rem;
  font-size: 1rem;
  min-width: 5rem;
}

.mobile-balance-section .balance-item:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.mobile-balance-section .balance-icon {
  font-size: 1rem;
}

.mobile-balance-section .balance-amount {
  font-size: 1rem;
  font-weight: 700;
}

/* Mobile menu text sizing */
.mobile-nav-content .nav-link {
  font-size: 1.1rem;
  padding: 0.6rem 1rem;
  margin: 0.2rem 0;
}

.mobile-nav-content .mobile-nav-btn {
  font-size: 1.1rem;
  padding: 0.6rem 1rem;
  margin: 0.2rem 0;
}

/* Options container spacing */
.mobile-nav-content .options {
  gap: 0.2rem;
}

/* Small viewport options */
@media (max-width: 480px) {
  .mobile-nav-content .option {
    margin: 2px 0;
    width: 100%;
    max-width: 300px;
  }
}


/* Dark mode for mobile balance */
.dark-mode .mobile-balance-section {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

.dark-mode .mobile-balance-section .balance-item {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
}

.dark-mode .mobile-balance-section .balance-item:hover {
  background: rgba(0, 0, 0, 0.3);
  border-color: rgba(255, 255, 255, 0.2);
}

/* User menu styling */
.user-menu {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.user-info {
  margin-bottom: 0.125rem;
}

.user-info small {
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 0.9;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.user-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.25rem;
}

.account-group {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.user-actions .btn {
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.user-actions .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Make BalanceDisplay and NotificationBell smaller */
.account-group .balance-display,
.account-group .notification-bell {
  transform: scale(0.85);
}

.account-group .balance-display *,
.account-group .notification-bell * {
  font-size: 0.8rem !important;
}

/* Center the logout button under the user actions */
.user-menu .btn {
  align-self: center;
  margin-top: 0.25rem;
}
</style>
